import { ReactNode, useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';

import { Button } from '@treezor/design-system';

import { TAlertKey } from '@/store/types/alert.types';
import { AccessExpirationAlert } from '@/features/dashboard-users/components/DashboardUserStatus/AccessExpirationAlert';

import { LogoutAlert } from './LogoutAlert';
import { OfflineAlert } from './OfflineAlert';
import { RootAlert } from './RootAlert';
import { ScopesAlert } from './ScopesAlert';
import { LogoutReminder } from './LogoutReminder';
import { useAlerts } from './useAlerts';

export type TAlert = Record<TAlertKey, { component: ReactNode; color: string }>;

interface IAlerts {
  maxNumber?: number;
}

export function Alerts({ maxNumber = 1 }: IAlerts) {
  const { t } = useTranslation();
  const { alerts: alertList, scopes } = useAlerts();

  const alertMapping: TAlert = {
    offlineWarning: { component: <OfflineAlert />, color: 'danger' },
    logoutWarning: { component: <LogoutAlert />, color: 'danger' },
    userScopesWarning: { component: <ScopesAlert scopes={scopes} />, color: 'warning' },
    userRootWarning: { component: <RootAlert />, color: 'warning' },
    logoutReminder: { component: <LogoutReminder />, color: 'warning' },
    accessExpirationWarning: { component: <AccessExpirationAlert />, color: 'warning' },
  };

  const alerts = alertList?.map((alert) => ({ key: alert, ...alertMapping[alert] }));

  const shouldStackAlert = alerts?.length > maxNumber;

  const [isStacked, setIsStacked] = useState(shouldStackAlert);

  useEffect(() => {
    if (!shouldStackAlert) {
      setIsStacked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts?.length]);

  const alertShadows = useCallback(
    () =>
      alerts
        ?.slice(1)
        ?.map((item, index) => (
          <div
            key={`alert-shadow-${index}`}
            style={{ width: `calc(100% - ${16 * (index + 1)}px)` }}
            className={clsx(
              'h-3 rounded-t-0 rounded-b-200 m-auto',
              item.color === 'danger' ? 'bg-tag-red' : 'bg-tag-orange',
              'shadow-100',
              'showShadows',
            )}
          />
        )),
    [alerts],
  );

  return (
    <div
      className="absolute top-4 right-4 z-[1001] w-[400px] cursor-pointer"
      onClick={() => {
        if (shouldStackAlert && isStacked) {
          setIsStacked((stacked) => !stacked);
        }
      }}
    >
      <div className={clsx('relative', !isStacked && 'flex flex-col gap-4')}>
        {alerts?.map(({ key, component }, index) => {
          const isFront = index === 0;

          return (
            <>
              <div
                key={`alert-item-${key}`}
                className={clsx(
                  `relative z-[${1001 + index}]`,
                  !isFront ? (isStacked ? 'stackItems' : 'unstackItems') : 'entrance',
                )}
              >
                {component}
              </div>
              {isFront && isStacked && alertShadows()}
            </>
          );
        })}
        {shouldStackAlert && !isStacked && (
          <div className="flex w-full justify-end items-center">
            <Button size="s" variant="tertiary" onClick={() => setIsStacked(true)}>
              {t('pages.cores.showLess')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
